import React from "react";
import { useNavigate } from "react-router-dom";
import { resolveImg } from "@/lib/api";
import { fmtRent, LABELS } from "@/lib/constants";
import { CompatibilityRing } from "@/components/CompatibilityRing";
import { TrustBadges } from "@/components/TrustBadges";
import { SaveButton } from "@/components/SaveButton";
import { MapPin, Briefcase } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1625241152315-4a698f74ceb7?w=600";

export function PersonCard({ person, savedIds, onSaveChange }) {
  const navigate = useNavigate();
  const p = person.profile || {};
  const img = resolveImg(p.photo_url) || FALLBACK;
  const score = person.compatibility?.score ?? 0;
  return (
    <div
      data-testid={`person-card-${person.user_id}`}
      onClick={() => navigate(`/person/${person.user_id}`)}
      className="group relative cursor-pointer overflow-hidden rounded-3xl bg-white shadow-card"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img src={img} alt={p.first_name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
        <div className="absolute right-3 top-3">
          <SaveButton itemType="person" itemId={person.user_id} savedIds={savedIds} onChange={onSaveChange} />
        </div>
        <div className="absolute left-3 top-3 rounded-2xl bg-white/95 p-1 shadow-soft">
          <CompatibilityRing score={score} size={44} stroke={4} />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
          <div className="flex items-center gap-2">
            <h4 className="font-heading text-xl font-extrabold leading-none">{p.first_name}{p.age ? `, ${p.age}` : ""}</h4>
          </div>
          <p className="mt-1 flex items-center gap-1 text-sm text-white/90"><Briefcase className="h-3.5 w-3.5" /> {p.occupation || "—"}</p>
          <p className="flex items-center gap-1 text-xs text-white/80"><MapPin className="h-3.5 w-3.5" /> {(p.preferred_localities?.[0]) || p.city || "—"}</p>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 p-3">
        <span className="rounded-full bg-brand-light px-2.5 py-1 text-xs font-bold text-brand-dark">
          {p.budget_min ? `${fmtRent(p.budget_min)}–${fmtRent(p.budget_max)}` : "Budget flexible"}
        </span>
        <TrustBadges verification={person.verification} />
      </div>
    </div>
  );
}

export function PersonRailCard({ person }) {
  const navigate = useNavigate();
  const p = person.profile || {};
  const img = resolveImg(p.photo_url) || FALLBACK;
  const score = person.compatibility?.score ?? 0;
  return (
    <div
      data-testid={`person-rail-${person.user_id}`}
      onClick={() => navigate(`/person/${person.user_id}`)}
      className="relative w-40 shrink-0 cursor-pointer overflow-hidden rounded-3xl bg-white shadow-card"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img src={img} alt={p.first_name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="absolute left-2 top-2 rounded-xl bg-white/95 p-0.5 shadow-soft">
          <CompatibilityRing score={score} size={38} stroke={4} />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
          <h4 className="font-heading text-base font-extrabold leading-tight">{p.first_name}{p.age ? `, ${p.age}` : ""}</h4>
          <p className="line-clamp-1 text-[11px] text-white/85">{p.occupation}</p>
        </div>
      </div>
    </div>
  );
}
